const format_en = `# Rich multimedia articles as standard format

## Topicles

Topicles are the JSON standard container for the dpa-digitalwires format.
All news content of the dpa and other agencies is transported in these containers.
This includes:

* dpa-Textfunk
* dpa-Bildfunk
* dpa-Weblines
* multimedia services of dpa

The core element of the dpa-digitalwires format is the so-called **topicle**.
Topicles combine aspects of multimedia articles (**articles**) on the one hand
and news packages (**topics**) on the other.

Topicles can thus represent all contents of the dpa as a coherent, self-contained,
JSON object. This includes:

* text-only articles from dpa-Textfunk
* individual images from dpa Bildfunk
* multimedia articles with text and pictures, e.g. from the Weblines and the multimedia services of the dpa
* packages of several texts, images and other assets e.g. from dpa-news

The dpa-digitalwires format is oriented towards the requirements of both editorial
use (e.g. through further processing in a CMS) and the automated publication of
content licensed for this purpose (e.g. the Weblines).

In the APIs of the digitalwires (see <Navlink exact to="/doc/apis">Documentation | APIs</NavLink>),
topicles are provided either individually or as part of a feed with multiple topicles.

The feed format is documented in the APIs section.

### Reference to other agencies

In addition to the content created by dpa, agencies distributed by dpa such as
Associated Press (AP) and Europa Press are also provided via dpa-digitalwires.

The services of the Katholischen Nachrichten-Agentur (KNA) are also available
for pilot customers via dpa-digitalwires.

If you are interested in content of other news agencies via dpa-digitalwires please
contact us by e-mail <a href="mailto:newslab-support@dpa.com">newslab-support@dpa.com</a>.

### Notes on the further development of the format

The dpa-digitalwires format is regularly extended in a backwards compatible way,
e.g. if further content from the dpa group can be made available via
dpa-digitalwires.

Currently, with version 0.8.x, additional metadata is provided in the
dpa-Textfunk and dpa-Bildfunk. This format extension enables the delivery of
these services in systems that previously received these services only via the
NewsML-G2 format.

This step is related to the planned transition of the dpa-Basisdienst to a
multimedia service in 2024, which will only be available via dpa-digitalwires.

### Overview

In the following, the components of the Topicle format are presented

- <NavLink exact to="/doc/format#the-common-core-across-all-content-types">The common core across all content types</NavLink>
- <Navlink exact to="/doc/format#extension-with-storytelling-elements">Extension with storytelling elements, e.g. for dpa-Text- and dpa-Bildfunk, dpa-Weblines, multimedia services and news packages</NavLink>
- <Navlink exact to="/doc/format#specific-extensions-and-adjustments">Specific extensions and adjustments</NavLink>

## The common core across all content types

Structurally, a topicle is divided into four parts:

* [Essential metadata](#essential-metadata)
* The [main object](#main-object-of-the-topicle-news-text), often a news text
* Optional [images / media objects associated](#associated-media-objects) with the main object
* [Categorical metadata](#categorical-metadata)

<a href="/img/topicle-dw.png" target="_blank"><img src="/img/topicle-dw.png" width=100%></a>

The keys used to represent the four parts are briefly described below.

These four parts are briefly described below.

### Essential metadata

Essential metadata is used to update and manage topicles.
They contain the following keys:

- **format**: The format of the entry. The name combined with a version number along the guidelines of semantic versioning (z.B. digitalwires@0.8.0).
- **entry_id**: The technical ID of the entry in dpa-digitalwires format. An opaque hash.
- **updated**: The timestamp of the update of the entry, time zone UTC ("zulu time").
- **urn**: The URN of the main object of the topicle.
- **version_created**: The timestamp of the last version of the main object of the topicle, with time zone, for dpa usually Europe/Berlin.
- **version**: The version of the main object of the topicle. For dpa contents, an integer.
- **type**: The type of the topicle. Currently always **composite**.
- **pubstatus**: Status of publication. One of three values: "usable", "withheld", "canceled".
- **signal**: Further notes on the occasion (e.g. correction/update) for publication. Corresponds to the <a href="https://cv.iptc.org/newscodes/signal/" target="_blank">G2-Signal</a>.
- **embargoed**: The timestamp of a blocking period to be observed, with time zone analogous to **version_created**.
- **language**: The language of the topicle.
- **urgency**: The urgency of the message. An integer between 1 and 9. Corresponds to the element with the same name in the IPTC News Architecture. For dpa content the values 1 = Blitzmeldung, 2 = Eilmeldung bis 6 are used. Other news agencies use other scales.

### Notes

#### **null** as a value

The value \`null\` is allowed for all keys if the corresponding information does not exist.

#### The relationship between **entry_id** and **urn**
* The **entry_id** is a low level ID that is relevant for technical transfer. You can use the **entry_id** to check whether you have already received this exact topicle once. A duplicate transfer can occur in exceptional cases, e.g. due to the asynchronous and distributed backend that generates the dpa-digitalwires format.
* The **urn**, i.e. the <a href="https://de.wikipedia.org/wiki/Uniform_Resource_Name" target="_blank">Uniform Resource Name</a> is the stable semantic ID of the main object of the topicle.

Topicles with different **urn** are different objects in the dpa editorial
system, although they may carry very similar texts.
Weblines articles are usually based on articles of the dpa-Textfunk, but have a
different **urn**, because e.g. the text has been changed or text/image links
have been added.
For news packets, on the other hand, the **urn** of the news packet is transported here.

#### The relationship between the timestamps **updated** and **version_created**

The relationship between the two timestamps **updated** and **version_created**
is very similar to the relationship between **entry_id** and **urn*.

- **updated** indicates when the topicle was last updated and is always specified in ISO8601 "Zuluzeit" (time zone UTC).
- **version_created** indicates when the last semantically relevant version of the main object of the topicle was created. **version_created** can be displayed e.g. on article pages, therefore the display is in ISO8601 with time zone information. The time information for dpa content is always in the time zone of Berlin.

#### Detect topicle status by **pubstatus**, **embargoed** and **signal**

Important notes about the publication status of a topicle result from three values:
- **pubstatus**: Indicates whether the topicle is basically \`usable\` or \`canceled\` and **must** therefore be depublished.
- **embargoed**: A timestamp here means that a blocking period must be kept until the specified time and the topicle may not be published.
- **signal**: Signaling various properties of changes to the content by the editors. At dpa, the signals \`sig:update\`, \`sig:repeat\` and \`sig:correction\` are currently sent. Other news agencies may send other signals.

### Main object of the topicle: News text

In most cases, the main object of a topicle is the text of an article or of a multimedia story.
The following keys or attributes are used:

  - **kicker**: The kicker of the article. A string.
  - **headline**: The headline of the article. A string.
  - **subhead**: The subhead of the article. A string.
  - **teaser**: The teaser of the article. A string
  - **byline**: The byline of the article. A string.
  - **dateline**: The dateline of the article. A string.
  - **article_html**: The actual article text as HTML \`<section>\`.
  - **infobox_html**: The infobox belonging to the article as HTML \`<section>\`.
  - **linkbox_html**: External links, which are usually displayed below the article text, as HTML \`<section>\`.
  - **creditline**: The creditline of the article. A string.
  - **copyrightnotice**: The copyright notice of the article. A string.
  - **usageterms**: The copyright terms of use of the article. A string.
  - **autopublishnotice**: The copyright notice for customers who publish consumer-ready content, e.g. the dpa-Weblines, automatically. Should be displayed on the detail page of the article. A string.
  - **ednotes**: Editorial notes to the article. A list of objects.
  - **descriptions**: List of descriptions. Currently mainly used in image wires for different caption variants (distinguished by the value of the key \`role\`). The actual texts are specified in the key \`description\`.

#### Notes

##### **article_html**, **infobox_html**, **linkbox_html**

The value of the attribute **article_html** contains the article in HTML5
representation. The following HTML tags are currently used there:

- **section**,**p**,**h2**,**strong**,**em** for outline, subheadings, bold and italic text markup.
- **a** for external and internal links.
- **ul**,**ol**, **li** for bullet point lists and numbered lists.
- **table**, **tbody**, **tr**, **td** for tables.

For other agencies/wires, other HTML5 tags may be used. For image-only wires,
\`article_html\` may remain empty.

##### Links in **article_html**, **infobox_html**, **linkbox_html**

- External links in article content are distinguished as **\`<a class="externalLink" href="...">...</a>\`**
- Internal links (i.e. links to other articles / topicles) in the article text are transported as **\`<a class="internalLink" data-uri="urn:newsml:dpa.com:20090101:xx-yy-zzz" href="">\`**. I.e. the URN of the linked topicle is transmitted in the attribute **data-uri**.


##### Escaping of unicode characters

As the example shows, unicode characters are transmitted escaped. This means that
the entire JSON response is "ASCII-encoded" and thus designed for maximum compatibility
with target systems. This type of encoding and escaping should be automatically
handled by your software.

\`\`\`javascript
{
  "article_html": "<section class=\\"main\\">
<p>Besser ist es, wenn Kinder die Bedeutung der W\\u00f6rter selbst erkunden, ... </p>
<p>Zum \\u00dcben bieten sich kleine Momente des Alltags an. Etwa ... Stern.</p>
</section>"
}
\`\`\`

#### Editorial notes: **ednotes**

The ednotes list contains notes from the news agency to the editors of the
recipient / customer. These notes my also include a refinement defining its kind
via the value of the key \`role\`.

The default structure of these objects is as follows:

- **role**: The role of the note. A qcode.
- **ednote**: The text of the note. A string.
- **is_publishable**: Indicates whether the note can be published after consideration by the receiving editorial office.

In the dpa wires,the following note roles are currently used (possibly more than once):

- **dpaednoterole:closingline**: The "traditional" closing line at the end of an article from the IPTC7901 format. Still included here for backwards compatibility.
- **dpaednoterole:contacts**: Contact persons of a facility addressed in the article.
- **dpaednoterole:correction**: Notes on the reason and form of a correction or withdrawal.
- **dpaednoterole:correctionshort**: Summary of the *dpaednoterole:correction*.
- **dpaednoterole:correction_consumerready**: Publishable, reader-friendly correction notice in the consumer-ready services.
- **dpaednoterole:dpacontacts**: Contact person(s) in the dpa editorial department for this article.
- **dpaednoterole:editorialnote**: General notes to the receiving editorial office.
- **dpaednoterole:edservice**: Additional service note on news content.
- **dpaednoterole:genrenote**: Note in connection with the genre of the news item.
- **dpaednoterole:picture**: Image/graphic reference.
- **dpaednoterole:embargo**: Embargo notice.

Content, especially from other news agencies, may contain other/additional
editorial references.

### Associated media objects

Associated media objects can be images, graphics, audios, videos or texts that
are related to the main object of the topicle. Within the topicle JSON, the textual
information and metadata of these objects are transported, the actual binary objects
are usually linked.

Currently, the associated media objects are predominantly images. Associated media
objects are transported as a list of **associations**. These have at least the
keys: **urn** and **type**.

#### Basics

An object in the **associations** list is made up of two parts:

- Basic metadata, including:
  - **urn**: The stable semantic ID of the media object.
  - **version**: The version of the media object. For dpa content an integer.
  - **version_created**: The timestamp of the last version of the media object with its time zone, for dpa content usually Europe/Berlin.
- **renditions**: List containing concrete objects of one or more media formats. These are made up of at least:
  - **url**: Here the binary object of the media object is referred to via URL. This URL is signed and has an expiry date of approx. 2 days. This should be sufficient to import the media object into your own CMS.
  - **mimetype**: The mimetype of the media object.

**Notes when accessing the url:** When accessing the **url**, especially for larger media objects, an HTTP status code 404 may be returned. For this case, a \`retry-after\`-Header is provided, which contains the timespan in seconds after which the media object may be accessed again via another call. We offer our customers a reference implementation on <a href="https://github.com/dpa-newslab/dpa-digitalwires-snippets/tree/main/association-requests" target="_blank">Github as open-source</a>.

#### Associated images

For each image there are the following keys:

  - **type**: The type of the media object. Here \`image\`.
  - **urn**: The URN of the image.
  - **rank**: The rank of the image in the order of all associated images. An integer.
  - **version**: The version of the image. For dpa content an integer.
  - **version_created**: The timestamp of the last version of the image with its time zone, for dpa content usually Europe/Berlin.
  - **is_featureimage**: An indicator that indicates whether the image is the feature/heroimage of the item. Currently assigned automatically for the first image and is then \`true\`, otherwise \`false\`.
  - **headline**: The title of the image.
  - **caption**: The caption of the image.
  - **creditline**: The credit line of the image. A string in the format "Photographer / Source" or "Photographer / Source / Distributor" (for images purchased from third-party agencies).
  - **renditions**: A list of objects with the specific image formats for the image.
    - **url**: Here the binary object of the image is referred to via URL. This URL is signed and has an expiry date of approx. 2 days. This should be sufficient to import the image into your own CMS.
    - **mimetype**: The mimetype of the image.
    - **width**: The width of the image in pixels.
    - **height**: The height of the image in pixels.
    - **size**: The length of the longer edge in pixels.
    - **fovealarea**: Image focus (with the keys \`left\`, \`right\`, \`top\`, \`bottom\` each measured from the upper left corner).

#### Notes

- By default, the images are delivered with a longer edge of \`2048px\`.
  - other edge lengths are possible on request and possibly for an extra charge.
- Some images with additional metadata on the so-called "image focus" (e.g. in the dpa-Weblines) can also be delivered scaled pixel-exactly, e.g. in the format \`1920x1080px\`.

#### Examples of articles

- <NavLink exact to="/doc/format#dpa-bildfunk-image">dpa-Bildfunk image</NavLink>

#### Associated videos, audios and graphics

The mono-media video, audio and graphics wires of dpa are also available in dpa-digitalwires format. These media types are currently not yet directly linked to a multimedia dpa article.

#### Associated graphics

For each graphic there are the following keys:

  - **type**: The type of the media object. Here \`graphic\`.
  - **urn**: The URN of the graphic.
  - **rank**: The rank of the graphic in the order of all associated graphics. An integer.
  - **version**: The version of the graphic. For dpa content an integer.
  - **version_created**: The timestamp of the last version of the graphic with its time zone, for dpa content usually Europe/Berlin.
  - **headline**: The title of the graphic.
  - **caption**: The caption of the graphic.
  - **renditions**: A list of objects for the graphic and the preview.
    - **url**: The binary object of the graphic is referenced here via URL. This URL is signed and has an expiration date of approx. 2 days. This should be sufficient to import the graphic into your own CMS.
    - **role**: The role of the graphic. Possible values: \`vector\`, \`preview\`.
    - **mimetype**: The mimetype of the graphic.
    - **width**: The width of the graphic in pixels.
    - **height**: The height of the graphic in pixels.
    - **size**: The length of the longer edge in pixels.

#### Examples of articles

- <NavLink exact to="/doc/format#graphic">Graphic</NavLink>

#### Associated audios

For each audio there are the following keys:

  - **type**: The type of the media object. Here \`audio\`.
  - **urn**: The URN of the audio.
  - **rank**: The rank of the audio in the order of all associated audios. An integer.
  - **version**: The version of the audio. For dpa content an integer.
  - **version_created**: The timestamp of the last version of the audio with its time zone, for dpa content usually Europe/Berlin.
  - **headline**: The title of the audio.
  - **caption**: The caption of the audio.
  - **renditions**: A list of objects with specific audio formats for the audio.
    - **url**: The binary object of the audio is referenced here via URL. This URL is signed and has an expiration date of approx. 2 days. This should be sufficient to import the audio into your own CMS.
    - **role**: The role of the audio. Possible values: \`raw\`.
    - **mimetype**: The mimetype of the audio.
    - **audiobitrate**: The bit rate of the audio in Kbit/s (kbps).
    - **audiosamplerate**: The sample rate of the audio in Hz.

#### Examples of articles

- <NavLink exact to="/doc/format#audio">Audio</NavLink>

#### Associated videos

For each video there are the following keys:

  - **type**: The type of the media object. Here \`video\`.
  - **urn**: The URN of the video.
  - **rank**: The rank of the video in the order of all associated videos. An integer.
  - **version**: The version of the video. For dpa content an integer.
  - **version_created**: The timestamp of the last version of the video with its time zone, for dpa content usually Europe/Berlin.
  - **headline**: The title of the video.
  - **caption**: The caption of the video.
  - **transcript**: The transcript of the video (optional).
  - **renditions**: A list of objects for the video and the preview.
    - **url**: The binary object of the video is referenced here via URL. This URL is signed and has an expiration date of approx. 2 days. This should be sufficient to import the video into your own CMS.
    - **role**: The role of the video. Possible values: \`poster\` (preview image), \`raw\` (video) and \`captions\` (subtitles).
    - **mimetype**: The mimetype of the rendition.
    - **width**: The width of the rendition in pixels.
    - **height**: The height of the rendition in pixels.
    - **size**: The length of the longer edge in pixels.

#### Examples of articles

- <NavLink exact to="/doc/format#video">Video</NavLink>

### Categorical metadata

Categorical classify the article into different categories and further information
on top of that. They are transported as a list of objects under the key **categories**.

The standard structure of these objects is as follows:

- **name**: The display name of the category.
- **type**: The type of the category as **dnltype:xxxx**.
- **qcode**: A qcode of the category in the form **Categoryprefix:ID** It is mainly used for types with "closed vocabularies".
- **rank**: The rank of the category in relation to the respective type.

Besides the mandatory \`type\`, \`name\` and \`qcode\` are optional. However,
at least one of the two keys must contain a value.

Other keys are possible depending on the type of category (z.B. \`role\`).

#### Notes on qcodes

QCodes have their origin in the IPTC News Architecture and are essentially
identical to the W3C's CURIEs. They consist of two parts: The category prefix
(or namespace), which defines the underlying vocabulary, and the actual ID,
which specifies the ID of a value in that vocabulary.

The IPTC has defined a 
<a href="https://www.iptc.org/standards/newscodes" target="_blank">variety of vocabularies</a>,
some of them are used here.
The news agencies themselves often define additional vocabularies of their own,
which are then given their own namespaces. For the most important vocabularies,
we offer CSV files with the ID values for download here.

A qcode is a stable and usually also cross-language uniform ID. Therefore,
if available, the \`qcode\` should be used on the receiver side for filtering
and routing purposes or similar and not the \`name\`.

### Categorical metadata in the current format core

The following is a listing of the categorical metadata currently defined in the format core.

- **dnltype:dpasubject**: A dpa first keyword or a mediatopic of the IPTC. A "closed vocabulary" of approx. 400 terms.
  - [Current version of the dpa first keywords as CSV file](/res/qcodes_dpasubject_de.csv)
  - [Current version of the IPTC mediatopics as CSV file](/res/qcodes_medtop_de.csv)
- **dnltype:desk**: A dpa resort. A "closed vocabulary" of 6 terms.
    - [Current version of the dpa resorts as CSV file](/res/qcodes_dpacat_de.csv)
- **dnltype:urgency**: Priority of the entry. Is identical to the key \`urgency\` of the topicle and is also listed here as a category for reasons of standardization.
- **dnltype:signal**: Structured references to special features of the entry that may require special attention (compare <Navlink exact to="/doc/format#detect-topicle-status-by-pubstatus-embargoed-and-signal">Detect topicle status by pubstatus, embargoed and signal</NavLink>).
- **dnltype:pubstatus**: Publication status
- **dnltype:wire**: A service identifier. A topicle always contains all the services to which it was sent. This may mean that services appear to which you are not subscribed. For dpa content, the \`dnltype:wire\` comes from a "closed vocabulary" of about 30 terms.
  - [Current version of the dpa service identifier as CSV file](/res/qcodes_wire.csv)
- **dnltype:rubric**: A dpa-Weblines or dpa-News rubric. A "closed vocabulary" of approx. 400 terms
  - [Current version of dpa rubrics as CSV file](/res/qcodes_rubric.csv)
- **dnltype:wikidata**: Wikidata concepts relevant to the article. With \`wikidata:\`-qcode
- **dnltype:subject**: A keyword from a "closed vocabulary" (e.g. IPTC mediatopics, keywords from other news agencies).
- **dnltype:geosubject**: A dpa geo keyword. For dpa articles from the following lists of qcodes:
  - [Current version of the dpa geo keywords (cities) as CSV file](/res/qcodes_dpageosubject_de.csv)
  - [Current version of the dpa geo keywords (countries) as CSV file](/res/qcodes_dpacountry_de.csv)
- **dnltype:genre**: Journalistic genre of news content. Used closed vocabularies depending on the type and source of the content.
  - [Current version of the dpa genre as CSV file](/res/qcodes_dpatextgenre_de.csv)
- **dnltype:provider**: Provider of the content.
- **dnltype:organization**: Organizations relevant to the news content. If applicable, with \`wikidata:\`-qcode.
- **dnltype:person**: Persons relevant to the news content. If applicable, with \`wikidata\`-qcode
- **dnltype:creator**: Creator of the entries
- **dnltype:contributor**: Other editorial staff members who contributed to the entry
- **dnltype:infosource**: Sources of the content (especially in the case of images), if applicable divided into source and provider (e.g. when taking over an image from an external news agency or the handout of a car manufacturer etc.)
- **dnltype:keyword**: A secondary keyword. A string.
- **dnltype:poi**: Point of interest to the news content.
- **dnltype:locationcreated**: Location where the news content was created (especially used for images).

#### Type specific extensions

If required, type-specific extensions are made to certain types of categories by
adding further keys.

Currently, this applies in essence to the following types:

- For **dnltype:infosource**, **dnltype:creator** and **dnltype:contributor** there may be an additional **role** that further specifies the function.
- With **dnltype:poi**, a GeoJson feature object is returned under the **geojson** key for locations in the news item. Currently with a GeoJson Point as value of the **geometry** key and at least the keys **name** and **formatted_address** as attributes of the **properties** key.

Other possible keys are: **country** for the country, **locality** for the municipality and **administrative_area_level_1** for the federal state (or abroad for the corresponding first administrative level below the federal state level).

An example:

\`\`\`javascript
"categories": [
  {
   "type": "dnltype:poi", 
   "name": "Bundesanwaltschaft, Brauerstra\u00dfe 30, 76135 Karlsruhe, Deutschland", 
   "qcode": null, 
   "geojson": {
    "geometry": {
     "type": "Point", 
     "coordinates": [
      8.3849201, 
      49.00189
     ]
    }, 
    "type": "Feature", 
    "properties": {
     "country": "Deutschland", 
     "formatted_address": "Brauerstra\u00dfe 30, 76135 Karlsruhe, Deutschland", 
     "name": "Bundesanwaltschaft", 
     "administrative_area_level_1": "Baden-W\u00fcrttemberg", 
     "locality": "Karlsruhe"
    }
   }
  },
  // next category...
]
\`\`\`

- With **dnltype:locationcreated**, a GeoJson feature object is returned under the **geojson** key for the location the news item was created or the picture was taken in. Attributes of the **properties**-key are usually **locality** for the municipality, **administrative_area_level_1** for the federal state (or abroad for the corresponding first administrative level below the federal state level) as well as **country** for the country. A **geometry**-key with exact coordinates is usually not provided.

An example:
\`\`\`
"categories": [
  {
   "type": "dnltype:locationcreated", 
   "name": "Herzogenaurach", 
   "qcode": null, 
   "geojson": {
    "type": "Feature", 
    "properties": {
      "locality": "Herzogenaurach",
      "administrative_area_level_1": "Bayern",
      "country": "Deutschland"
    }
   }
  },
  // next category...
]
\`\`\`

## Extension with storytelling elements

Multimedia stories can be enhanced with storytelling elements. Currently this
is done in dpa-Weblines, the dpa-Themenwelten and the dpa-Faktenchecks.

Essentially, these extensions (as opposed to text only services in section 1) make
the representation in the **article_html** and **infobox_html** attributes
richer. In addition, there are extensions in the categorical metadata.

The additional content includes:

* social media content like Tweets and YouTube videos
* interactive charts, graphics, tables based on Datawrapper
* dpa-Weblines-Addons
* Images positioned in the text
* Interactive explanatory graphics

The articles contain hints on where in the text which element can be embedded
in a meaningful way. In addition, the articles - like normal news articles also contain some HTML
tags, e.g. subheadings and lists. In 2021 dpa-infocom GmbH changed the production in the dpa-Weblines,
dpa-Faktenchecks and dpa-Themenwelten to multimedia stories.

<a href="/img/embed-grafik.png" target="_blank"><img src="/img/embed-grafik.png" width=100%></a>

### Embedded content via Web Components

In order to transport rich content in topicles, so-called Web Components are used.

Web Components are an internet standard that is implemented by all
[current browsers](https://caniuse.com/?search=web%20components). They offer a
number of advantages. The most important are:

- W3C standard - Web Components are an internet standard of the W3C. This means that the technology is implemented by all current browsers and is very likely to be supported in the long term.
- A simple syntax that integrates seamlessly into HTML5. Any tag that contains the - character is interpreted by HTML5 parsers / browsers as a web component. The Web Components used within the topicles have the prefix dnl-, for example "dnl-twitterembed" for a tweet.
- Data protection friendly - Unless Javascript is also executed, Web Components are ignored by browsers, and any links contained in attributes are not called up. This way you can guarantee that e.g. tweets and YouTube videos are not displayed until all data protection precautions have been taken, e.g. your readers have agreed to the use of external content.

This gives them maximum flexibility to decide how rich articles are processed or presented to their readers.

You can:
- Convert Web Components into their CMS during import and translate them into their component technology. (**suggested procedure**)
- Pass web components unchanged through to the app or website and deploy them there using appropriate code.

A reference implementation for the individual Web Components of the dpa-newslab
is available on [Github published as open-source](https://github.com/dpa-newslab/dpa-digitalwires-embeds).
On the basis of this reference implementation or their own already existing
Web Components customers can then also realize an integration of e.g. the
embedded content into their CMP (i.e. Opt-In, Two-Click, Opt-Out etc.).

#### Currently implemented Web Components

The following Web Components are currently implemented and released for use by
by the editorial teams of the dpa-Weblines, the dpa-Themenwelten and
the dpa-Faktenchecks. This does not mean that they are already in widespread use.

#### Twitter Embeds: dnl-twitterembed

A tweet is embedded via the component **dnl-twitterembed**. This has a single
attribute **src** in which the URL to the tweet is transported.

Component: **dnl-twitterembed**

- **src**: Link to the tweet

\`\`\` html
<dnl-twitterembed src="https://twitter.com/gautamkhimlal/status/1168582968098488320"></dnl-twitterembed>
\`\`\`

The reference implementation attempts to load the tracking-free variant of the tweet.

#### YouTube Embeds: dnl-youtubeembed

A YouTube video is embedded via the component **dnl-youtubeembed**. This has a
single attribute **src**, in which the URL to the video is transported.

Component: **dnl-youtubeembed**

- **src**: Link to the Youtube-Video

\`\`\` html
<dnl-youtubeembed src="https://www.youtube-nocookie.com/embed/R3VMW6fxK6Y"></dnl-youtubeembed>
\`\`\`

In the **src** attribute, the link to the YouTube video selected by the editors is
transported. Usually this should be a link to the cookie-free variant.
However, this cannot be guaranteed.

#### Datawrapper Embeds: dnl-dwchart

Interactive visualisations are regularly created by dpa / dpa-infocom with the
help of tools from the German provider Datawrapper, which specialises in this field.
These can be roughly divided into the categories chart, map and table.

All three types are embedded with the help of the component **dnl-dwchart**.
Currently, this component has an attribute **src** that contains the link to the visualisation.

Component: **dnl-dwchart**

- **src**: Link zur Datawrapper-Visualisierung

\`\`\` html
<dnl-dwchart src="https://datawrapper.dwcdn.net/OEAsW/1"></dnl-dwchart>
\`\`\`

#### Interactive web graphics: dnl-wgchart

Interactive explanatory graphics are regularly created by dpa-infocom GmbH.
They are embedded with the help of the component **dnl-wgchart**

Component: **dnl-wgchart**

- **src**: Link to the web graphic

\`\`\` html
<dnl-wgchart src="https://webgrafik.dpa-addons.com/customers/client/aws/my/html/index.html?pathSrc=aktuell/2020_10_12/autoreifen/cont&amp;nameCSS=style_1_2.css&amp;versionScript=2.2"></dnl-wgchart>
\`\`\`


There are two ways to display the graphic responsively:

1. Render the web graphic as \`iframe\` and implement a custom logic to (re-)scale it by using a postMessage-listener (compare <a href="https://github.com/dpa-newslab/dpa-digitalwires-embeds/blob/main/src/js/modules/DNLWebchart.js#L86" target="_blank">reference implementation</a>)
2. If you have already obtained web graphics from dpa-Shop or you're using dpa-infocom's \`dpa.js\` in other contexts, we recommend that you use \`dpa.js\` as well:

\`\`\` html
<div data-app="dpa-webgrafik" data-embed-type="pym" data-src="https://webgrafik.dpa-addons.com/customers/client/aws/my/html/index.html?pathSrc=aktuell/2020_10_12/autoreifen/cont&amp;nameCSS=style_1_2.css&amp;versionScript=2.2"></div>
<script type='text/javascript' src='https://core.dpa-infocom.net/js/dpa.js'></script>
\`\`\`

#### Positioned images: dnl-image

The **dnl-image** component indicates the recommended position of an image in the
article. The **ref** attribute indicates which of the
<NavLink exact to="/doc/format#associated-images">associated images</a> can be
inserted reasonable at this position when importing into the CMS or onto the website.

Recommended procedure:

1. Import associated images into the CMS
2. Search article HTML for \`<dnl-image>\` elements
3. Replace \`<dnl-image>\` components with a variant of the referenced image imported into the CMS

Component: **dnl-image**

- **ref**: URN of the positioned image. Used to find the associated image
- **caption**: Caption when using the image at the specified position
- **creditline**: The Creditline of the image

\`\`\` html
<dnl-image ref="urn:newsml:dpa.com:20090101:201109-99-266444" caption="Gef\u00e4hrliche Mission: Khim Lal Gautam beim Sammeln von Daten am Mount Everest" creditline="Tshiring Jangbu Sherp/privat/dpa"></dnl-image>
\`\`\`

## Specific extensions and adjustments

### dpa-Weblines

#### Brief characterisation

dpa-Weblines is consumer-ready content that is authorized for automated
publication in the Inter-, Intra- or Extranet as well as in Apps etc.

Editorial offices are also happy to use them as prepared, online-suitable
multimedia content, which is then imported into the CMS system as "easy-to-publish"
content and regionalised or otherwise adapted by the editorial team with minimal effort.

dpa-Webline articles are **additionally classified**, i.e. sorted into a so-called
"rubric tree" of approx. 200 rubrics. A news item can be assigned to several
rubrics, e.g. "Sport" and "Topthemen / Hintergründe".

dpa-Weblines are usually illustrated, but in new newsworthy situations it may
exceptionally happen that an article is only updated with picture material in a
second version as soon as pictures are available.

#### dpa-Webline extension: **dnltype:rubric**

**dnltype:rubric** is the dpa-Weblines rubrication. Here, the additional key **is_current**
is used to signal that the article currently still bears this rubric. The news
management in the dpa-Weblines removes rubrics (especially in "Topthemen") if the
article is no longer relevant from the editorial point of view.

The set of rubrics is also a closed vocabulary:

- **dnltype:rubric**: A dpa Weblines or dpa News rubric. A "Closed Vocabulary" of approx. 400 terms.
  - [Current version of the dpa rubrics as CSV file](/res/qcodes_rubric.csv)

Example:

\`\`\` javascript
{
  "type": "dnltype:rubric",
  "name": "weblines.infoline_rs.wissenschaft",
  "qcode": "dnlpath:weblines.infoline_rs.wissenschaft",
  "is_current": true
}
\`\`\`

#### dpa-Webline extension publishable correction notice: **dpaednoterole:correction_consumerready**

In order to correct errors transparently, dpa-Weblines articles with \`signal\` (especially \`sig:correction\`) can (but do not have to!) contain a correction notice explicitly intended for publication. It is included in the list of \`ednotes\` and has the following format:

\`\`\` javascript
{
  "role": "dpaednoterole:correction_consumerready",
  "ednote": "Die Stadt Lianjiang liegt in der Provinz Guangdong, nicht in der Provinz Guangzhou (10.07.2023, 15.08 Uhr)",
  "is_publishable": true
}
\`\`\`

This reference can be placed nearby the article and can be made available to readers as well. We recommend writing a short source note before the text of the \`ednote\` (e.g. "Note by the dpa editorial office:", "Editor's note:").

#### dpa-Webline extension geographical relevance: **dnltype:scope**

**dnltype:scope** is a geographical area of Germany for which an article is
considered relevant.

This relevance allows recipients of the topicle to assign the content to local
or regional editions or corresponding verticals.

The value of the qcode of the type **dnltype:scope** contains the 8-digit 
so called "Allgemeinen Gemeindeschlüssel" (AGS) as ID.
The qcode of the smallest management unit selected in each case is transported.
To derive the IDs of higher management units, use the hierarchical key structure of the AGS.

Typically, the dpa-Weblines editorial office selects at least districts or
district-free cities as scope.

Example:
\`\`\` javascript
{
  "type": "dnltype:scope", 
  "name": "Flensburg", 
  "qcode": "dpax-kgs8:01001000"
}
\`\`\`

#### Examples of articles

- <NavLink exact to="/doc/format#dpa-weblines-text-image-article">dpa-Weblines Text-Image-Article</NavLink>
- <NavLink exact to="/doc/format#dpa-weblines-multimedia-story">dpa-Weblines multimedia story</NavLink>

### dpa-Textfunk and other text services

#### Brief characterisation

The term dpa-Textfunk summarizes the text services of the dpa editorial office.
In addition to the dpa-Basisdienst, it includes the dpa-Landesdienste, the
dpa-Themendienst, the news for children, and corresponding foreign news services.

#### Services of other news agencies

The text services of dpa-AFX and news aktuell are also available as topicles via dpa-digitalwires.

For pilot customers, text and image content from the KNA (Katholischen Nachrichten-Agentur)
is also available by arrangement with the KNA.

If you are interested please contact us by e-mail <a href="mailto:newslab-support@dpa.com">newslab-support@dpa.com</a>.

This also applies if you would like to receive text services from other news
agencies via dpa-digitalwires. We will then try to find a solution in cooperation
with the respective news agency.

#### The previous provision

So far, these services are delivered via satellite or FTP in the formats
IPTC7901 or NewsML-G2 delivered.

In the dpa-digitalwires, all information contained in the IPTC7901 format was
already transported by June 2023. At the same time, however, the actual text was
no longer transmitted as pure text (in a restricted character set and a line
length of 69 characters), but as HTML5 markup. This has already greatly
facilitated the transfer into digital systems.

Since the end of June 2023, all relevant information from the NewsML G2 format
has also been transported. In addition, the \`usageTerms\`, the editorial
notes (\`ednote\`) and (\`genre\`) and \`description\` tags are
now included in the form documented above.

This means that nothing stands in the way of replacing the old format with the
dpa-digitalwires.

#### Textfunk extension: **notepad**

In the notepad, additional information is transported that helps an editorial
team during research, or represent further information.

A distinction is made between a public and a non-public part of the notepad.

The public part can be found under **public_html**, the part not intended for
publication under **nonpublic_html**. In the key **header_html** the header of
the notepad is is transported.

Parts of the information of the public part of the notepad is already delivered
in the core of the format: In **linkbox_html** you will find the links mentioned
in the section Internet mentioned links as HTML. Many other components are in
the \`ednotes\` list.

The non-public part of the notepad may only be shared, if it is ensured that the
content provided is not automatically published.
In the case of consumer-ready products such as dpa-Weblines, information not
intended for publication can be made accessible in this case.

\`\`\`javascript
"notepad": {
      "header_html": "<header><h3>Notizblock</h3></header>",
      "nonpublic_html": "<section class=\\"np-nonpublic\\">
<p class=\\"np-constraint\\"Die folgenden Informationen sind nicht zur Ver\u00f6ffentlichung bestimmt</p>
<h4>Kontakte</h4>
<ul>
<li>Autoren: Michel Winde (Br\u00fcssel), +32\u00a02\u00a0234\u00a06466, &lt;<a href=\\"mailto:winde.michel@dpa.com\\">winde.michel@dpa.com</a>&gt;, Martina Herzog (Berlin)</li>
<li>Redaktion: Hans-Hermann Nikolei (Berlin), +49 30 285231302, &lt;<a href=\\"mailto:politik-ausland@dpa.com\\">politik-ausland@dpa.com</a>&gt;</li>
<li>Foto: Newsdesk, +49 30 2852 31515, &lt;<a href=\\"mailto:foto@dpa.com\\">foto@dpa.com</a>&gt;</li></ul></section>",
      "public_html": null
}
\`\`\`

#### Examples of articles

- <NavLink exact to="/doc/format#dpa-textfunk-article">dpa-Textfunk article</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-summary">dpa-Textfunk summary</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-news-overview">dpa-Textfunk news overview</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-correction">dpa-Textfunk correction</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-withdrawal">dpa-Textfunk withdrawal</NavLink>

### dpa-Bildfunk and other image services

#### Brief characterisation

The term dpa-Bildfunk summarizes the image services of the dpa editorial office.
In addition to the dpa-Bilderdienst, this includes the dpa-Zentralbild, the
Bilderdienst of the Themendienstes and the news for children, as well as
corresponding foreign news services.

#### Services of other agencies

The AP European Photo Wire and the original picture service of news aktuell are
also available as topicles via dpa-digitalwires.

For pilot customers, Bildfunkdienste of the KNA (Katholischen Nachrichten-Agentur)
are also available in agreement with the KNA. Please contact us for further information.

This also applies if you would like to receive image services from other news
agencies via dpa-digitalwires. We will then try to find a solution in cooperation
with the respective news agency.

#### The previous provisioning formats

Traditionally, these services are provided via satellite or FTP in NewsML-G2
formats or as pure binary objects (JPEG).

In the dpa-digitalwires, images of the dpa-Bildfunk are delivered as topicles
with an empty \`article_html\` and an \`association\` to one or more \`renditions\`
of the image. The metadata of the image is transported as metadata of the topicle.

#### Bildfunk extension image description: **descriptions**, **caption**

In addition to the \`caption\` in the \`associations\` part of an image, the
description is also in the \`descriptions\` list. Here, there are usually two
variants that can be distinguished by the \`role\`:

- \`drol:caption\`: Caption of the image (mostly identical with \`caption\`)
- \`dparol:caption-extended\`: Extended description. The caption expanded to include legal and, if applicable, editorial information and the credit.

#### dpa-Bildfunk-Genres

The type \`dnltype:genre\` is used to provide information about the journalistic
type of the image. For dpa images this includes especially:

- \`dpascn\`: Image scene classification.
- \`dpaimagekind\`: Classification of the image (including current, archive, handout).
  - [Current version of dpa image classifications as CSV file](/res/dpaimagekind.csv)

#### Bildfunk extension **locationcreated**

The type \`dnltype:locationcreated\` serves to provide information about the location where an image was taken.
The structure is as follows:

- **name**: Name of the location the image was taken.
- **geojson**: A GeoJSON object with further information regarding the location. Often just includes \`properties\` and no \`geometry\`.

#### Bildfunk extension **infosource**

Via \`dnltype:infosource\` details about the origin of an image are provided.
Information about the role of an \`infosource\`, is shown by the \`role\` key.
For dpa images there are:

- \`isrol:contentSource\`: Image source.
- \`dpaisrol:supplier\`: Other suppliers involved (such as other news agencies etc.).

#### Note

A suitable form for the presentation of all involved suppliers can be
found in the \`creditline\` of the \`association\` or, in case of
positioned images, in the attribute of the Web Component.

### dpa-Themenwelten

#### Brief characterisation

The dpa-Themenwelten are consumer-ready content that is released for automated publication on the inter-, intra- or extranet as well as in apps, etc. The articles are evergreen content.
Their life cycle is designed for the longest possible publication period, which distinguishes them from the predominantly daily updated content of the dpa-Weblines. This involves some editorial and technical peculiarities with regard to delivery as well as updating.

The following sections contain information on the used metadata fields. For further recommendations on the technical und content-related setup, please also check <a target="_blank" href="https://service.dpa-infocom.de/de/support/solutions/articles/202000031079-dpa-themenwelten-aus-technischer-sicht">the support page of dpa-Themenwelten</a>.

#### dpa-Themenwelten extension dpaflextag:pc.topic

The assignment of an article to a concrete Themenwelt is done via a flextag, which is represented in the \`categories\` and contains the following keys:

- **type**: The type of category, always \`dnltype:flextag\`.
- **name**: The name of the Themenwelt, for example, can be displayed on a theme page.
- **qcode**: QCode of the Themenwelt in the format \`dpaflextag:pc.topic.<identifier>\`. The identifier (e.g. \`laufen\` or \`rueckenschmerzen\`) can be used as URL slug.

Example:
\`\`\`javascript
{
  "type": "dnltype:flextag",
  "name": "Wein",
  "qcode": "dpaflextag:pc.topic.wein"
}
\`\`\`

#### dpa-Themenwelten extension dpaflextag:pc.lifecycle

Some Themenwelten-articles discuss topics that are no longer up-to-date (e.g. Corona) or turn out not to be as click-worthy as initially thought. In such cases, articles might no longer be maintained by the dpa-Themenwelten-team. This is indicated by \`dpaflextag:pc.lifecycle.archived\`:

\`\`\`javascript
{
    "type": "dnltype:flextag",
    "name": "archiviert",
    "qcode": "dpaflextag:pc.lifecycle.archived"
}
\`\`\`

Human readable notes on the reason for such archiving can be found in the \`ednotes\`-list as \`dpaednoterole:correction\` and \`dpaednoterole:correctionshort\`: 

\`\`\`
"ednotes": [{
    "role": "dpaednoterole:correction",
    "ednote": "Dieser Themenwelten-Artikel wird seit 16. Oktober 2023 nicht mehr weiter aktualisiert.",
    "is_publishable": true
}, {
    "role": "dpaednoterole:correctionshort",
    "ednote": "Dieser Themenwelten-Artikel wird seit 16. Oktober 2023 nicht mehr weiter aktualisiert.",
    "is_publishable": true
}, ...]
\`\`\`

You can find detailed information on handling dpa-Themenwelten archiving here: <a href="https://service.dpa-infocom.de/de/support/solutions/articles/202000031079-dpa-themenwelten-aus-technischer-sicht#Archivierung-von-Themenwelt-Artikeln" target="_blank">https://service.dpa-infocom.de/de/support/solutions/articles/202000031079-dpa-themenwelten-aus-technischer-sicht#Archivierung-von-Themenwelt-Artikeln</a>.


#### dpa-Themenwelten extension dpaflextag:userneed

From May 2025 onwards, dpa-Themenwelten articles contain information about the user need they are tailored to. Details on the model used can be found in the [dpa Innovation Blog](https://innovation.dpa.com/2024/04/04/user-needs-verdoppeln-leser-engagement-und-steigern-abo-rate/).

The user need is represented in the \`categories\` and contains the following keys:

- **type**: The type of category, always \`dnltype:flextag\`.
- **name**: The name of the category.
- **qcode**: qcode of the category.

The following qcodes are possible:
- **dpaflextag:userneed.updateme**
- **dpaflextag:userneed.educateme**
- **dpaflextag:userneed.givemeperspective**
- **dpaflextag:userneed.helpme**
- **dpaflextag:userneed.divertme**
- **dpaflextag:userneed.inspireme**

#### Deprecated dpa-Themenwelten extensions

The metadata of dpa-Themenwelten articles is continuously optimized. Therefore, new metadata can be added and existing one can be removed.

The following previously assigned metadata types are now obsolete:
- Typ \`dnltype:publishadvice\`
  - qcode-Präfix \`dpa-limbic-system:*\`
  - qcode-Präfix \`advice-importance:*\`
  - qcode-Präfix \`advice-lifetime:*\`
- Typ \`dnltype:flextag\`
  - qcode-Präfix \`pc.evergreen.*\`

### Multimedia and feature services of dpa

#### Brief characterisation

Topicles in multimedia and feature services contain, in addition to the text,
references to to associated images as associations.
Structurally, they are identical to topicles in the
<NavLink exact to="/doc/format#dpa-weblines">dpa-Weblines</NavLink> section

Unlike the content in dpa's consumer-ready products (such as dpa-Weblines),
these services are generally not approved for automatic publication and must be
curated/edited by an editorial team before they can be published.

These services are mainly offered by the foreign language editorial offices of
dpa but the AP News Service is also available, as a multimedia service.

#### Difference: Feature service / multimedia service

Currently, dpa mainly produces the foreign language services as multimedia
services. These differ in the degree to which they are enriched with images.
While every news item in the foreign language consumer news is illustrated, this is only the
case to a certain extent in the news services. The images are often added in
subsequent versions of a topicle.

#### Services of other news agencies

In the dpa-digitalwires, the AP European News Service (in English) and the
KNA-Webline are also available as multimedia services.

#### Outlook

In the traditional IPTC7901 and NewsML-G2 formats, the delivery of multimedia services is not possible.

Therefore, the multimedia Basis- and Landesdienste planned for 2024 will only
be available via the dpa-digitalwires as a multimedia service.

For a transitional period, they will still be provided as mono-media text services.

#### Examples of articles

- <NavLink exact to="/doc/format#english-multimediadienst">English Multimediadienst</NavLink>
- <NavLink exact to="/doc/format#ap-european-news-service">AP European News Service</NavLink>

### Appendix Examples of articles

- <NavLink exact to="/doc/format#multimedia-dpa-basisdienst-article">Multimedia dpa-Basisdienst article</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-article">dpa-Textfunk article</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-summary">dpa-Textfunk summary</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-news-overview">dpa-Textfunk news overview</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-correction">dpa-Textfunk correction</NavLink>
- <NavLink exact to="/doc/format#dpa-textfunk-withdrawal">dpa-Textfunk withdrawal</NavLink>
- <NavLink exact to="/doc/format#dpa-bildfunk-image">dpa-Bildfunk image</NavLink>
- <NavLink exact to="/doc/format#dpa-weblines-text-image-article">dpa-Weblines Text-Image-Article</NavLink>
- <NavLink exact to="/doc/format#dpa-weblines-multimedia-story">dpa-Weblines multimedia story</NavLink>
- <NavLink exact to="/doc/format#english-multimediadienst">English Multimediadienst</NavLink>
- <NavLink exact to="/doc/format#ap-european-news-service">AP European News Service</NavLink>
- <NavLink exact to="/doc/format#simple-example-article-with-all-html-tags-used">Simple example article with all HTML tags used</NavLink>

#### Multimedia dpa-Basisdienst article

<codeblockbrowser to='/res/bdt-multimedial.json'></codeblockbrowser>

#### dpa-Textfunk article

<codeblockbrowser to='/res/textfunk-meldung.json'></codeblockbrowser>

#### dpa-Textfunk summary

<codeblockbrowser to='/res/textfunk-zusammenfassung.json'></codeblockbrowser>

#### dpa-Textfunk news overview

<codeblockbrowser to='/res/textfunk-nachrichtenueberblick.json'></codeblockbrowser>

#### dpa-Textfunk correction

<codeblockbrowser to='/res/berichtigung.json'></codeblockbrowser>

#### dpa-Textfunk withdrawal

<codeblockbrowser to='/res/kill.json'></codeblockbrowser>

#### dpa-Bildfunk image

<codeblockbrowser to='/res/bildfunk-bild.json'></codeblockbrowser>

#### dpa-Weblines text image article

<codeblockbrowser to='/res/weblines-text-bild-artikel.json'></codeblockbrowser>

#### dpa-Weblines multimedia story

<codeblockbrowser to='/res/weblines-multimediale-story.json'></codeblockbrowser>

#### English Multimediadienst

<codeblockbrowser to='/res/englischer-multimediadienst.json'></codeblockbrowser>

#### AP European News Service

<codeblockbrowser to='/res/ap-weltnachrichten.json'></codeblockbrowser>

#### Graphic

<codeblockbrowser to='/res/grafik.json'></codeblockbrowser>

#### Audio

<codeblockbrowser to='/res/audio.json'></codeblockbrowser>

#### Video

<codeblockbrowser to='/res/video_vtt.json'></codeblockbrowser>

#### Simple example article with all HTML tags used

<codeblockbrowser to='/res/beispielmeldung.json'></codeblockbrowser>
`

export { format_en }


// WEBPACK FOOTER //
// ./src/all_cards/docu/format_en.js